<template>
  <BittsLoading :is-loading="!isReady">
    <div class="api-filters-tab">
      <ApiTabEmptyState
        v-if="!filterCards.length"
        title="No filters yet"
        description="Create a filter to control exactly which of your populations and partner populations the API returns. Your filters and their IDs will appear here."
        create-cta-text="Create filter"
        :icon="['fak', 'filter']"
        @create="openCreate('filters')"
      />
      <template v-else>
        <ApiResourceCard
          v-for="filter in filterCards"
          :key="filter.id"
          :name="filter.name"
          :status="filter.status"
          :status-text="filter.statusText"
        >
          <template #actions>
            <ApiResourceCardActions
              action-text="Edit"
              :action-icon="['fak', 'edit']"
              :resource-name="filter.name"
              @action="openDetail('filters', filter.id)"
            />
          </template>
          <ApiCopyValue :value="filter.id" :label="`ID for ${filter.name}`" />
        </ApiResourceCard>
      </template>
    </div>
  </BittsLoading>
</template>

<script setup lang="ts">
import { BittsLoading } from '@crossbeam/bitts';

import { useHead } from '@unhead/vue';
import { useDocumentVisibility, useIntervalFn } from '@vueuse/core';
import { storeToRefs } from 'pinia';
import { computed, watch } from 'vue';

import { useIntegrationProfileStore } from '@/stores';
import {
  API_FILTER_INTEGRATION_TYPE,
  type ApiFilterCalculationStatusType,
} from '@/types/integration_profiles';

import ApiCopyValue from '../components/ApiCopyValue.vue';
import ApiResourceCard from '../components/ApiResourceCard.vue';
import ApiResourceCardActions from '../components/ApiResourceCardActions.vue';
import ApiTabEmptyState from '../components/ApiTabEmptyState.vue';
import type { ApiResourceStatusType } from '../types';
import useApiRoutes from '../useApiRoutes';

useHead({ title: 'API Filters - Crossbeam' });

const { openCreate, openDetail } = useApiRoutes();
const integrationProfileStore = useIntegrationProfileStore();
const { ready: isReady } = storeToRefs(integrationProfileStore);

const CALCULATION_BADGES = {
  pending: { status: 'warning', statusText: 'Pending' },
  running: { status: 'warning', statusText: 'Pending' },
  complete: null,
  failed: { status: 'danger', statusText: 'Failed' },
} as const satisfies Record<
  ApiFilterCalculationStatusType,
  { status: ApiResourceStatusType; statusText: string } | null
>;

const filters = computed(() =>
  integrationProfileStore.getIntegrationProfilesByType(
    API_FILTER_INTEGRATION_TYPE,
  ),
);

const POLL_INTERVAL_MS = 30 * 1000;
const documentVisibility = useDocumentVisibility();

const hasCalculatingFilter = computed(() =>
  filters.value.some((filter) => {
    const status = filter.calculation_status?.status;
    return status === 'pending' || status === 'running';
  }),
);

const shouldPoll = computed(
  () => hasCalculatingFilter.value && documentVisibility.value === 'visible',
);

const { pause, resume } = useIntervalFn(
  integrationProfileStore.refreshIntegrationProfileStore,
  POLL_INTERVAL_MS,
  { immediate: false },
);

watch(shouldPoll, (isPolling) => (isPolling ? resume() : pause()), {
  immediate: true,
});

const filterCards = computed(() =>
  filters.value.map((filter) => {
    const badge = filter.calculation_status
      ? CALCULATION_BADGES[filter.calculation_status.status]
      : undefined;
    return {
      id: filter.profile_id,
      name: filter.name || 'Untitled filter',
      status: badge?.status,
      statusText: badge?.statusText ?? '',
    };
  }),
);
</script>

<style scoped lang="pcss">
@reference "../../../css/theme.css";

.api-filters-tab {
  @apply flex flex-col gap-16;
}
</style>
